import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AccessTime, DeleteOutline, ExpandMore, HelpOutline } from '@mui/icons-material';
import { ClarificationItem, ClarificationResponse } from '../../client/SmartAssistantBackendClient';
import { useErrorContext } from '../../contexts/ErrorContext';
import { useLoadingContext } from '../../contexts/LoadingContext';
import { VoiceChat } from './VoiceChat';
import { useCurrentThreadContext } from '../../contexts/CurrentThreadContext';

export function ChatMessageSender() {
  const {
    history,
    awaitingResponse,
    queuedMessages,
    cancelQueuedMessage,
    addMessage,
  } = useCurrentThreadContext();
  const { errors } = useErrorContext();
  const { loading, loadingText } = useLoadingContext();
  const assistantError = errors.assistants ?? null;

  const [textDraft, setTextDraft] = useState('');
  const [clarificationDrafts, setClarificationDrafts] = useState<Record<string, string>>({});
  const [clarificationOpen, setClarificationOpen] = useState(true);

  const latestClarification = useMemo(() => {
    for (let i = history.length - 1; i >= 0; i -= 1) {
      const msg = history[i];
      if (!msg) continue;
      if (msg.role === 'tool') {
        continue;
      }
      const isToolCallOnly = msg.role === 'assistant'
        && (msg.toolCalls?.length ?? 0) > 0
        && !msg.content
        && !(msg.clarification?.questions?.length);
      if (isToolCallOnly) {
        continue;
      }
      if (msg.role !== 'assistant') return undefined;
      if (msg.clarification?.questions?.length) {
        return msg.clarification;
      }
      return undefined;
    }
    return undefined;
  }, [history]);

  const showClarificationPanel = Boolean(latestClarification?.questions?.length);

  const handleToggleClarification = useCallback(() => {
    setClarificationOpen(prev => !prev);
  }, []);

  const handleClarificationDraftChange = useCallback((key: string, value: string) => {
    setClarificationDrafts(prev => (prev[key] === value ? prev : ({ ...prev, [key]: value })));
  }, []);

  const buildClarificationPayload = useCallback(() => {
    if (!latestClarification?.questions?.length) return undefined;
    return new ClarificationResponse({
      messageId: latestClarification.messageId,
      questions: latestClarification.questions.map((q, idx) => new ClarificationItem({
        id: q?.id,
        question: q?.question,
        answer: q?.id ? (clarificationDrafts[q.id] ?? '').trim() : (clarificationDrafts[`missing-${idx}`] ?? '').trim(),
      })),
    });
  }, [clarificationDrafts, latestClarification]);

  const handleSend = useCallback(() => {
    const trimmed = textDraft.trim();
    const payload = buildClarificationPayload();
    const hasClarification = Boolean(payload?.questions?.length);
    if (!trimmed && !hasClarification) return;
    addMessage(trimmed, payload, { broadcast: true });
    setTextDraft('');
    setClarificationDrafts({});
  }, [addMessage, buildClarificationPayload, textDraft]);

  const canSend = (textDraft.trim().length > 0 || showClarificationPanel) && !awaitingResponse;
  const inputLocked = awaitingResponse;

  const handleVoiceChange = useCallback((value: string) => {
    setTextDraft(value);
  }, []);

  return (
    <Box
      sx={{
        gridArea: 'input',
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'background.paper',
        pt: 1,
        pb: 1,
        zIndex: 1,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        Suchbox
      </Typography>
      {showClarificationPanel && latestClarification?.questions?.length ? (
        <Box
          sx={(theme) => ({
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(145deg, rgba(30, 64, 175, 0.18), rgba(24, 28, 36, 0.7))'
              : 'linear-gradient(145deg, rgba(226, 232, 240, 0.7), rgba(255, 255, 255, 0.95))',
            color: theme.palette.text.primary,
          })}
        >
          <Box
            role="button"
            onClick={handleToggleClarification}
            aria-expanded={clarificationOpen}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(145deg, rgba(37, 99, 235, 0.12), rgba(24, 28, 36, 0.85))'
                : 'linear-gradient(145deg, rgba(59, 130, 246, 0.12), rgba(255, 255, 255, 0.95))',
              cursor: 'pointer',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 10px 24px rgba(0,0,0,0.35)'
                : '0 10px 24px rgba(15,23,42,0.08)',
              transition: 'all 120ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 14px 28px rgba(0,0,0,0.4)'
                  : '0 14px 28px rgba(15,23,42,0.12)',
              },
            })}
          >
            <Box display="flex" alignItems="center" gap={1.25}>
              <HelpOutline fontSize="small" color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                Klärungsfragen
              </Typography>
              <Chip
                size="small"
                label={latestClarification.questions.length}
                color="primary"
                sx={{ fontWeight: 700 }}
              />
            </Box>
            <ExpandMore
              sx={{
                transition: 'transform 150ms ease',
                transform: clarificationOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Box>
          {clarificationOpen && (
            <Stack spacing={1.5}>
              {latestClarification.questions.map((q, qIndex) => {
                const qId = q?.id;
                const draftKey = qId ?? `missing-${qIndex}`;
                const existingAnswer = q?.answer;
                const fieldValue = clarificationDrafts[draftKey] ?? existingAnswer ?? '';
                return (
                  <Box key={draftKey} sx={{ display: 'grid', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                      {q?.question ?? `Frage ${qIndex + 1}`}
                    </Typography>
                    <TextField
                      fullWidth
                      label="Antwort"
                      placeholder="Antwort (optional)"
                      value={fieldValue}
                      disabled={Boolean(awaitingResponse) || !qId}
                      helperText={!qId
                        ? 'Diese Frage hat keine ID und kann nicht beantwortet werden.'
                        : 'Antwort optional'}
                      onChange={(ev) => {
                        handleClarificationDraftChange(draftKey, ev.target.value);
                      }}
                      multiline
                      minRows={2}
                    />
                  </Box>
                );
              })}
              <Button
                variant="contained"
                size="small"
                onClick={handleSend}
                disabled={Boolean(awaitingResponse)}
                sx={{ alignSelf: 'flex-start' }}
              >
                Fragen beantworten
              </Button>
            </Stack>
          )}
        </Box>
      ) : null}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
        <Box width="100%" display="flex" flexDirection="column" gap={1}>
          {queuedMessages.length > 0 && (
            <Box
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.28)' : 'rgba(15, 118, 110, 0.35)'}`,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(145deg, rgba(16, 185, 129, 0.14), rgba(24, 28, 36, 0.6))'
                  : 'linear-gradient(145deg, rgba(14, 116, 144, 0.08), rgba(255, 255, 255, 0.98))',
                color: theme.palette.text.primary,
              })}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Warteschlange (wird nach der aktuellen Antwort gesendet)
              </Typography>
              <Stack spacing={0.75}>
                {queuedMessages.map((item, idx) => (
                  <Box key={item.id} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                    <Typography variant="caption" color="text.secondary">
                      {idx + 1}.
                    </Typography>
                    <AccessTime
                      sx={(theme) => ({
                        fontSize: 14,
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(16, 185, 129, 0.9)'
                          : 'rgba(15, 118, 110, 0.9)',
                      })}
                    />
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                      {item.text}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Aus Warteschlange entfernen"
                      onClick={() => cancelQueuedMessage(item.id)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          <TextField
            fullWidth
            label="Frage oder Suchbegriff"
            placeholder="Wonach suchst du?"
            value={textDraft}
            disabled={inputLocked}
            InputProps={{
                  endAdornment: (
                <InputAdornment position="end">
                  <VoiceChat
                    currentText={textDraft}
                    onChange={handleVoiceChange}
                    onCommit={handleSend}
                  />
                </InputAdornment>
              ),
            }}
            onChange={(ev) => setTextDraft(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' && !ev.shiftKey) {
                ev.preventDefault();
                if (canSend) {
                  handleSend();
                }
              }
            }}
            multiline
            minRows={2}
            maxRows={6}
          />
        </Box>

        {loading && !assistantError && (
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 220 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              {loadingText || 'Lade Daten …'}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={handleSend}
          disabled={!canSend}
          sx={{ px: 4 }}
        >
          Senden
        </Button>
        {awaitingResponse && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary">
              Antwort wird geladen …
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
